// src/components/PlotEventsTable.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import apiClient from "../utils/apiClient";
import { formatDate } from "../utils/dateUtils";

const PlotEventsTable = ({ plot_uuid, year, onEditEvent }) => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/plots/${plot_uuid}/events`);
      setEvents(response.data || []);
    } catch (err) {
      console.error("Failed to fetch plot events:", err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [plot_uuid]);

  useEffect(() => {
    if (!events?.length) {
      setFilteredEvents([]);
      return;
    }

    const filtered = events.filter((event) => {
      const ws = event.window_start ? new Date(event.window_start) : null;
      const we = event.window_end ? new Date(event.window_end) : null;
      const de = event.date_executed ? new Date(event.date_executed) : null;

      if (!ws && !we && !de) return false;

      const startYear = ws?.getFullYear();
      const endYear = we?.getFullYear();
      const execYear = de?.getFullYear();

      return (
        startYear === year ||
        endYear === year ||
        execYear === year ||
        (startYear && endYear && startYear < year && endYear > year)
      );
    });

    // Sort by executed date if completed, or window midpoint if upcoming
    filtered.sort((a, b) => {
      const getSortDate = (event) => {
        if (event.status === "done" && event.date_executed) {
          return new Date(event.date_executed);
        }
        if (event.window_start && event.window_end) {
          // Calculate window midpoint
          const start = new Date(event.window_start);
          const end = new Date(event.window_end);
          return new Date((start.getTime() + end.getTime()) / 2);
        }
        return new Date(0); // Fallback
      };

      return getSortDate(a) - getSortDate(b);
    });

    setFilteredEvents(filtered);
  }, [events, year]);

  const getEventTypeColor = (status) => {
    if (status === "done") return "#8b4513"; // Brown for done
    if (status === "skipped") return "#808080"; // Gray for skipped
    return "#d2691e"; // Orange for planned
  };

  const getCropName = (event) => {
    if (event.planted?.crop?.name) return event.planted.crop.name;
    const notes = event.notes || "";
    if (notes.toLowerCase().startsWith("sow cover crop:")) {
      const name = notes.slice("sow cover crop:".length).trim();
      if (name) return name;
    }
    return "Unknown Crop";
  };

  const formatEventDate = (event) => {
    if (event.date_executed) {
      return formatDate(event.date_executed);
    }
    if (event.window_start && event.window_end) {
      const start = new Date(event.window_start);
      const end = new Date(event.window_end);
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    }
    return "No date";
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading events...</Typography>
      </Box>
    );
  }

  if (!filteredEvents?.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" sx={{ textAlign: "center", color: "text.secondary" }}>
          No tasks for {year}.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} className="plot-events-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="column-status">Status</TableCell>
              <TableCell className="column-task-type">Task Type</TableCell>
              <TableCell className="column-crop">Crop</TableCell>
              <TableCell className="column-date">Date/Window</TableCell>
              <TableCell className="column-amount">Amount</TableCell>
              <TableCell className="column-output">Output</TableCell>
              <TableCell className="column-notes">Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEvents.map((event) => (
              <TableRow
                key={event.event_uuid}
                onClick={() => onEditEvent(event)}
                className="clickable-row"
              >
                <TableCell className="column-status">
                  <Chip
                    label={
                      event.status === "done" 
                        ? "Completed" 
                        : event.status === "skipped" 
                        ? "Skipped" 
                        : "Planned"
                    }
                    size="small"
                    sx={{
                      backgroundColor: getEventTypeColor(event.status),
                      color: "white",
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell className="column-task-type">{event.event_type?.name || "Unknown Task"}</TableCell>
                <TableCell className="column-crop">{getCropName(event)}</TableCell>
                <TableCell className="column-date">{formatEventDate(event)}</TableCell>
                <TableCell className="column-amount">{event.amount || "-"}</TableCell>
                <TableCell className="column-output">{event.output || "-"}</TableCell>
                <TableCell className="column-notes">{event.notes || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PlotEventsTable;
